/**
 * Batch Results Analyzer
 *
 * Displays aggregate statistics and detailed results for completed batches.
 * Includes comparison views for prompt variations and persona combinations.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as CostIcon,
  Speed as LatencyIcon,
  Chat as TurnsIcon,
  Star as QualityIcon,
  Security as CBCIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material';
import {
  getAdvancedSimulationBatchResults,
  getAdvancedSimulationBatchStatus
} from '../../../services/advancedSimulationApi';

function StatCard({ title, value, subtitle, icon, color = 'primary' }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" color={`${color}.main`}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function BatchResultsAnalyzer({ batchId, token }) {
  const [batch, setBatch] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadResults();
  }, [batchId, token]);

  const loadResults = async () => {
    setLoading(true);
    try {
      const [batchData, resultsData] = await Promise.all([
        getAdvancedSimulationBatchStatus(batchId, token),
        getAdvancedSimulationBatchResults(batchId, token)
      ]);
      setBatch(batchData);
      setResults(resultsData);
    } catch (err) {
      setError('Failed to load batch results');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!batch || !results) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        No results available
      </Alert>
    );
  }

  const stats = batch.aggregateStats || {};
  const runs = results.runs || [];
  const cbcStats = stats.cbcStats || null;

  // Check if this batch had CBC testing enabled
  const hasCbcTesting = runs.some(run => run.configSnapshot?.cbcConfig?.enabled);

  // Group runs by CBC preset for comparison
  const runsByCbcPreset = runs.reduce((acc, run) => {
    const cbcConfig = run.configSnapshot?.cbcConfig;
    const preset = cbcConfig?.enabled ? (cbcConfig.preset || 'custom') : 'disabled';
    if (!acc[preset]) {
      acc[preset] = [];
    }
    acc[preset].push(run);
    return acc;
  }, {});

  // Calculate CBC preset statistics
  const cbcPresetStats = Object.entries(runsByCbcPreset).map(([preset, presetRuns]) => {
    const completedRuns = presetRuns.filter(r => r.status === 'completed');
    const avgEffectiveness = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.cbcMetrics?.cbcEffectivenessScore || 0), 0) / completedRuns.length
      : 0;
    const avgTherapeuticRatio = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.cbcMetrics?.therapeuticContentRatio || 0), 0) / completedRuns.length
      : 0;
    const totalDriftEpisodes = completedRuns.reduce((sum, r) => sum + (r.metrics?.cbcMetrics?.driftEpisodeCount || 0), 0);
    const roleAbandonmentCount = completedRuns.filter(r => r.metrics?.cbcMetrics?.roleAbandonmentDetected).length;
    const avgResistanceCount = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.cbcMetrics?.resistanceCount || 0), 0) / completedRuns.length
      : 0;

    return {
      preset,
      totalRuns: presetRuns.length,
      completedRuns: completedRuns.length,
      avgEffectiveness,
      avgTherapeuticRatio,
      totalDriftEpisodes,
      roleAbandonmentCount,
      avgResistanceCount
    };
  });

  // Group runs by variation for comparison
  const runsByVariation = runs.reduce((acc, run) => {
    const variation = run.configSnapshot?.promptVariation || 'default';
    if (!acc[variation]) {
      acc[variation] = [];
    }
    acc[variation].push(run);
    return acc;
  }, {});

  // Calculate variation statistics
  const variationStats = Object.entries(runsByVariation).map(([variation, variationRuns]) => {
    const completedRuns = variationRuns.filter(r => r.status === 'completed');
    const avgQuality = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.qualityScore || 0), 0) / completedRuns.length
      : 0;
    const avgLatency = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.avgLatencyMs || 0), 0) / completedRuns.length
      : 0;
    const avgTurns = completedRuns.length > 0
      ? completedRuns.reduce((sum, r) => sum + (r.metrics?.totalTurns || 0), 0) / completedRuns.length
      : 0;
    const totalCost = variationRuns.reduce((sum, r) => sum + (r.metrics?.estimatedCost || 0), 0);

    return {
      variation,
      totalRuns: variationRuns.length,
      completedRuns: completedRuns.length,
      failedRuns: variationRuns.filter(r => r.status === 'failed').length,
      avgQuality,
      avgLatency,
      avgTurns,
      totalCost
    };
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Batch Results: {batch.name}
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Total Runs"
            value={stats.totalRuns || runs.length}
            subtitle={`${stats.successfulRuns || 0} successful`}
            icon={<TurnsIcon color="primary" />}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Avg Quality Score"
            value={(stats.averageQuality || 0).toFixed(2)}
            subtitle={`σ = ${(stats.qualityStdDev || 0).toFixed(2)}`}
            icon={<QualityIcon color="success" />}
            color="success"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Avg Latency"
            value={`${(stats.averageLatency || 0).toFixed(0)}ms`}
            subtitle={`σ = ${(stats.latencyStdDev || 0).toFixed(0)}ms`}
            icon={<LatencyIcon color="info" />}
            color="info"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            title="Total Cost"
            value={`$${(stats.totalCost || 0).toFixed(4)}`}
            subtitle={`$${((stats.totalCost || 0) / Math.max(1, runs.length)).toFixed(4)} per run`}
            icon={<CostIcon color="warning" />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="By Variation" />
          <Tab label="All Runs" />
          <Tab label="Quality Analysis" />
          {hasCbcTesting && <Tab label="CBC Comparison" icon={<CBCIcon />} iconPosition="start" />}
        </Tabs>
      </Paper>

      {/* By Variation Tab */}
      {activeTab === 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Variation</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Success Rate</TableCell>
                <TableCell align="right">Avg Quality</TableCell>
                <TableCell align="right">Avg Latency</TableCell>
                <TableCell align="right">Avg Turns</TableCell>
                <TableCell align="right">Total Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {variationStats.map((vs) => (
                <TableRow key={vs.variation}>
                  <TableCell>
                    <Chip label={vs.variation} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="right">
                    {vs.completedRuns}/{vs.totalRuns}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={(vs.completedRuns / vs.totalRuns) * 100}
                        sx={{ width: 60, height: 8, borderRadius: 4 }}
                        color={vs.failedRuns > 0 ? 'warning' : 'success'}
                      />
                      {((vs.completedRuns / vs.totalRuns) * 100).toFixed(0)}%
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Quality Score (0-10)">
                      <span>{vs.avgQuality.toFixed(2)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{vs.avgLatency.toFixed(0)}ms</TableCell>
                  <TableCell align="right">{vs.avgTurns.toFixed(1)}</TableCell>
                  <TableCell align="right">${vs.totalCost.toFixed(4)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* All Runs Tab */}
      {activeTab === 1 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Run ID</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Therapist</TableCell>
                <TableCell>Variation</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Turns</TableCell>
                <TableCell align="right">Quality</TableCell>
                <TableCell align="right">Latency</TableCell>
                <TableCell align="right">Cost</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run._id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                      {run.runId?.substring(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>{run.configSnapshot?.clientPersonaKey}</TableCell>
                  <TableCell>{run.configSnapshot?.therapistPersonaKey}</TableCell>
                  <TableCell>
                    <Chip
                      label={run.configSnapshot?.promptVariation || 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={run.status}
                      size="small"
                      color={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">{run.metrics?.totalTurns || 0}</TableCell>
                  <TableCell align="right">
                    {run.metrics?.qualityScore?.toFixed(2) || 'N/A'}
                  </TableCell>
                  <TableCell align="right">
                    {run.metrics?.avgLatencyMs?.toFixed(0) || 0}ms
                  </TableCell>
                  <TableCell align="right">
                    ${(run.metrics?.estimatedCost || 0).toFixed(4)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Quality Analysis Tab */}
      {activeTab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Quality Score Distribution
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {runs.filter(r => r.status === 'completed').map((run) => (
                    <Tooltip
                      key={run._id}
                      title={`${run.configSnapshot?.clientPersonaKey} × ${run.configSnapshot?.therapistPersonaKey}: ${run.metrics?.qualityScore?.toFixed(2) || 'N/A'}`}
                    >
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: 1,
                          bgcolor: `rgba(76, 175, 80, ${(run.metrics?.qualityScore || 0) / 10})`,
                          border: '1px solid rgba(0,0,0,0.1)'
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Best Performing Combinations
                </Typography>
                <Table size="small">
                  <TableBody>
                    {runs
                      .filter(r => r.status === 'completed' && r.metrics?.qualityScore)
                      .sort((a, b) => (b.metrics?.qualityScore || 0) - (a.metrics?.qualityScore || 0))
                      .slice(0, 5)
                      .map((run, idx) => (
                        <TableRow key={run._id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {idx === 0 && <TrendingUpIcon color="success" fontSize="small" />}
                              #{idx + 1}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {run.configSnapshot?.clientPersonaKey} × {run.configSnapshot?.therapistPersonaKey}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={run.metrics?.qualityScore?.toFixed(2)}
                              size="small"
                              color="success"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Needs Improvement
                </Typography>
                <Table size="small">
                  <TableBody>
                    {runs
                      .filter(r => r.status === 'completed' && r.metrics?.qualityScore)
                      .sort((a, b) => (a.metrics?.qualityScore || 0) - (b.metrics?.qualityScore || 0))
                      .slice(0, 5)
                      .map((run, idx) => (
                        <TableRow key={run._id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {idx === 0 && <TrendingDownIcon color="error" fontSize="small" />}
                              #{idx + 1}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {run.configSnapshot?.clientPersonaKey} × {run.configSnapshot?.therapistPersonaKey}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={run.metrics?.qualityScore?.toFixed(2)}
                              size="small"
                              color="warning"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          {/* Issues Detected */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Issues Detected
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Echo Events
                    </Typography>
                    <Typography variant="h5" color={stats.echoCount > 0 ? 'warning.main' : 'success.main'}>
                      {runs.reduce((sum, r) => sum + (r.metrics?.echoCount || 0), 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Role Reversals
                    </Typography>
                    <Typography variant="h5" color={stats.roleReversalCount > 0 ? 'error.main' : 'success.main'}>
                      {runs.reduce((sum, r) => sum + (r.metrics?.roleReversalCount || 0), 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Failed Runs
                    </Typography>
                    <Typography variant="h5" color={stats.failedRuns > 0 ? 'error.main' : 'success.main'}>
                      {runs.filter(r => r.status === 'failed').length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      Avg Goals Achieved
                    </Typography>
                    <Typography variant="h5" color="primary.main">
                      {(runs
                        .filter(r => r.status === 'completed')
                        .reduce((sum, r) => sum + (r.metrics?.goalsAchieved || 0), 0) /
                        Math.max(1, runs.filter(r => r.status === 'completed').length)
                      ).toFixed(1)}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* CBC Comparison Tab */}
      {activeTab === 3 && hasCbcTesting && (
        <Grid container spacing={2}>
          {/* CBC Summary Cards */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CBCIcon color="primary" />
              CBC Preset Comparison
            </Typography>
          </Grid>

          {/* Summary stats from batch-level aggregation */}
          {cbcStats && (
            <>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Avg Therapeutic Ratio</Typography>
                    <Typography variant="h4" color={(cbcStats.avgTherapeuticRatio || 0) >= 0.7 ? 'success.main' : 'warning.main'}>
                      {((cbcStats.avgTherapeuticRatio || 0) * 100).toFixed(0)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Avg CBC Effectiveness</Typography>
                    <Typography variant="h4" color={(cbcStats.avgCbcEffectiveness || 0) >= 70 ? 'success.main' : 'warning.main'}>
                      {(cbcStats.avgCbcEffectiveness || 0).toFixed(0)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Total Drift Episodes</Typography>
                    <Typography variant="h4" color={(cbcStats.totalDriftEpisodes || 0) > 5 ? 'warning.main' : 'text.primary'}>
                      {cbcStats.totalDriftEpisodes || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="body2" color="text.secondary">Role Abandonments</Typography>
                    <Typography variant="h4" color={(cbcStats.roleAbandonmentCount || 0) > 0 ? 'error.main' : 'success.main'}>
                      {cbcStats.roleAbandonmentCount || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Comparison Table by Preset */}
          <Grid item xs={12}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>CBC Preset</TableCell>
                    <TableCell align="right">Runs</TableCell>
                    <TableCell align="right">Avg Effectiveness</TableCell>
                    <TableCell align="right">Therapeutic Ratio</TableCell>
                    <TableCell align="right">Drift Episodes</TableCell>
                    <TableCell align="right">Role Abandonments</TableCell>
                    <TableCell align="right">Avg Resistance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cbcPresetStats.sort((a, b) => b.avgEffectiveness - a.avgEffectiveness).map((ps) => (
                    <TableRow
                      key={ps.preset}
                      sx={{
                        bgcolor: ps.avgEffectiveness >= 80 ? 'success.50' :
                                 ps.avgEffectiveness >= 60 ? 'warning.50' : 'error.50'
                      }}
                    >
                      <TableCell>
                        <Chip
                          icon={<CBCIcon />}
                          label={ps.preset.charAt(0).toUpperCase() + ps.preset.slice(1)}
                          size="small"
                          color={ps.preset === 'disabled' ? 'default' : 'primary'}
                          variant={ps.preset === 'disabled' ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {ps.completedRuns}/{ps.totalRuns}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={ps.avgEffectiveness}
                            sx={{ width: 60, height: 8, borderRadius: 4 }}
                            color={ps.avgEffectiveness >= 80 ? 'success' : ps.avgEffectiveness >= 60 ? 'warning' : 'error'}
                          />
                          <Typography variant="body2" sx={{ minWidth: 30 }}>
                            {ps.avgEffectiveness.toFixed(0)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {(ps.avgTherapeuticRatio * 100).toFixed(0)}%
                      </TableCell>
                      <TableCell align="right">
                        {ps.totalDriftEpisodes}
                      </TableCell>
                      <TableCell align="right">
                        {ps.roleAbandonmentCount > 0 ? (
                          <Chip
                            icon={<WarningIcon />}
                            label={ps.roleAbandonmentCount}
                            size="small"
                            color="error"
                          />
                        ) : (
                          <Chip
                            icon={<SuccessIcon />}
                            label="0"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {ps.avgResistanceCount.toFixed(1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Best/Worst Performers by CBC */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SuccessIcon color="success" />
                  Best CBC Performance
                </Typography>
                <Table size="small">
                  <TableBody>
                    {runs
                      .filter(r => r.status === 'completed' && r.configSnapshot?.cbcConfig?.enabled)
                      .sort((a, b) => (b.metrics?.cbcMetrics?.cbcEffectivenessScore || 0) - (a.metrics?.cbcMetrics?.cbcEffectivenessScore || 0))
                      .slice(0, 5)
                      .map((run, idx) => (
                        <TableRow key={run._id}>
                          <TableCell>#{idx + 1}</TableCell>
                          <TableCell>
                            <Chip
                              label={run.configSnapshot?.cbcConfig?.preset || 'custom'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {run.configSnapshot?.clientPersonaKey}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={run.metrics?.cbcMetrics?.cbcEffectivenessScore || 0}
                              size="small"
                              color="success"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon color="warning" />
                  Needs Improvement
                </Typography>
                <Table size="small">
                  <TableBody>
                    {runs
                      .filter(r => r.status === 'completed' && r.configSnapshot?.cbcConfig?.enabled)
                      .sort((a, b) => (a.metrics?.cbcMetrics?.cbcEffectivenessScore || 0) - (b.metrics?.cbcMetrics?.cbcEffectivenessScore || 0))
                      .slice(0, 5)
                      .map((run, idx) => (
                        <TableRow key={run._id}>
                          <TableCell>#{idx + 1}</TableCell>
                          <TableCell>
                            <Chip
                              label={run.configSnapshot?.cbcConfig?.preset || 'custom'}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            {run.configSnapshot?.clientPersonaKey}
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={run.metrics?.cbcMetrics?.cbcEffectivenessScore || 0}
                              size="small"
                              color="warning"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Completion Info */}
      <Box sx={{ mt: 3, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <Typography variant="caption" color="text.secondary">
          Started: {new Date(batch.createdAt).toLocaleString()}
        </Typography>
        {batch.completedAt && (
          <Typography variant="caption" color="text.secondary">
            Completed: {new Date(batch.completedAt).toLocaleString()}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default BatchResultsAnalyzer;
